import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHandler } from '../PlayerHandler';
import { GetPlayerGamesByUserIdAndFilterCommandRequest, GameListItem } from '../../../../Core/Api/Api';

interface PlayerMatchHistoryProps {
  userId: string;
}

const formatTimeAgo = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} Days Ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} Weeks Ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} Months Ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const getGameModeLabel = (game: GameListItem): string => {
  if (game.isSinglePlayer) return 'Single Mode';
  if (game.botGame && game.ranked) return 'VS Bot (Ranked)';
  if (game.botGame) return 'VS Bot';
  return 'VS Mode';
};

const getStatusColor = (status: string | undefined): string => {
  switch (status) {
    case 'Completed': return '#22c55e';
    case 'InProgress': return '#3b82f6';
    case 'Expired': return '#f59e0b';
    case 'Cancelled': return '#ef4444';
    default: return '#6b7280';
  }
};

const PlayerMatchHistory: React.FC<PlayerMatchHistoryProps> = ({ userId }) => {
  const { theme, isMobile } = useTheme();
  const [games, setGames] = useState<GameListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 5;

  const fetchGames = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const handler = new PlayerHandler();
      const request = new GetPlayerGamesByUserIdAndFilterCommandRequest({
        id: userId,
        pageNumber: page,
        amountPerPage: pageSize,
        orderBy: [{ field: 'CreatedAt', ascending: false }],
      });
      const response = await handler.getPlayerGames(request);
      setGames(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching player games:', error);
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchGames(1);
  }, [fetchGames]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchGames(page);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '10px',
      padding: isMobile ? '12px' : '16px',
      marginBottom: '0px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    title: {
      fontSize: '16px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '12px',
    },
    gameRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: `1px solid ${theme.border}`,
      flexWrap: 'wrap' as const,
      gap: '8px',
    },
    gameMode: {
      fontWeight: 600,
      fontSize: '14px',
      color: theme.text,
    },
    gameTime: {
      fontSize: '13px',
      color: theme.textSecondary,
    },
    gameStatus: {
      fontSize: '12px',
      fontWeight: 600,
      padding: '4px 10px',
      borderRadius: '12px',
    },
    gameScore: {
      fontSize: '13px',
      color: theme.text,
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      paddingTop: '16px',
    },
    pageBtn: {
      padding: '6px 12px',
      fontSize: '13px',
      fontWeight: 500,
      border: `1px solid ${theme.inputBorder}`,
      borderRadius: '6px',
      background: theme.inputBg,
      color: theme.text,
      cursor: 'pointer',
    },
    pageBtnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '2px solid rgba(59, 130, 246, 0.3)',
      borderTop: '2px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    empty: {
      color: theme.textSecondary,
      fontSize: '14px',
      fontStyle: 'italic' as const,
      textAlign: 'center' as const,
      padding: '24px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>Match History</div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
          <div style={styles.spinner} />
        </div>
      ) : games.length === 0 ? (
        <div style={styles.empty}>No matches found</div>
      ) : (
        <>
          {games.map((game) => {
            const statusColor = getStatusColor(game.status);
            return (
              <div key={game.id} style={styles.gameRow}>
                <div>
                  <div style={styles.gameMode}>{getGameModeLabel(game)}</div>
                  <div style={styles.gameTime}>{formatTimeAgo(game.createdAt)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={styles.gameScore}>
                    {game.playerOneScore != null ? `${game.playerOneScore}` : '-'}
                    {game.playerTwoScore != null ? ` vs ${game.playerTwoScore}` : ''}
                  </div>
                  <span style={{
                    ...styles.gameStatus,
                    background: `${statusColor}22`,
                    color: statusColor,
                  }}>
                    {game.status}
                  </span>
                </div>
              </div>
            );
          })}

          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: theme.text }}>
                {currentPage} / {totalPages}
              </span>
              <button
                style={{ ...styles.pageBtn, ...(currentPage >= totalPages ? styles.pageBtnDisabled : {}) }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PlayerMatchHistory;
